import React, { useState, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import DashboardCard from "../../components/DashboardCard";
import DashboardChart from "../../components/DashboardChart";
import OrdersTable from "../../components/OrdersTable";
import ActivityLog from "../../components/ActivityLog";
import { adminServices } from "../../services/AdminServices";
import backButtonIcon from "../../icons/inventory/dashboard/back-button.png";
import YearToDateSalesReport from "./reports/YearToDateSalesReport";
import SalesPeriodCoveredReport from "./reports/SalesPeriodCoveredReport";
import TotalProfitReport from "./reports/TotalProfitReport";
import ProcurementCostReport from "./reports/ProcurementCostReport";
import NotificationBell from "../../components/NotificationBell";
import { useAdminNotifications } from "../../hooks/useAdminNotifications";

const Dashboard = ({ onViewOrder }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [facility, setFacility] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [activeView, setActiveView] = useState("overview"); // 'overview' | 'ytd-sales' | 'sales-period' | 'total-profit' | 'procurement-cost'
  const [salesViewMode, setSalesViewMode] = useState("Monthly"); // 'Daily' | 'Weekly' | 'Monthly'

  // Admin Notifications Hook
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
  } = useAdminNotifications();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (dateFrom) {
          params.append("dateFrom", dateFrom);
        }
        if (dateTo) {
          params.append("dateTo", dateTo);
        }
        if (facility) {
          params.append("facilityId", facility);
        }
        const qs = params.toString() ? `?${params.toString()}` : "";

        const [dashboardRes, metricsRes] = await Promise.all([
          adminServices.getData(`Dashboard${qs}`),
          adminServices.getData(`Dashboard/metrics${qs}`)
        ]);
        setDashboardData(dashboardRes?.data ?? null);
        setMetricsData(metricsRes?.data ?? null);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setDashboardData(null);
        setMetricsData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [facility, dateFrom, dateTo]);

  useEffect(() => {
    const fetchColonies = async () => {
      try {
        const response = await adminServices.postData("sp/v2/getallfacilities", {});
        const data = response?.data;
        if (Array.isArray(data)) {
          const options = [{ value: "", label: "Facility" }];
          data.forEach((fac) => {
            const id = fac.facilityId;
            const name = fac.facilityName;
            if (id && name) {
              options.push({
                value: String(id),
                label: `${id} - ${name}`
              });
            }
          });
          setFacilities(options);
        }
      } catch (error) {
        console.error("Error fetching colonies:", error);
        setFacilities([{ value: "", label: "Facility" }]);
      }
    };
    fetchColonies();
  }, []);
  const dashboard = dashboardData?.dashboard ?? null;
  const m = metricsData?.metrics ?? metricsData;
  const formatCurrency = (v) => (v != null && !isNaN(Number(v)) ? `PHP${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null);
  const dashboardCards = [
    { title: "Total active PDL Merchant", value: m?.totalActivePDLMerchants ?? m?.totalActivePdlMerchants ?? "0", icon: require("../../icons/inventory/dashboard/pdl-merchant.png"), iconColor: "#94E9B8" },
    { title: "Year to Date Sales", value: formatCurrency(m?.yearToDateSales) ?? "PHP0.00", icon: require("../../icons/inventory/dashboard/daily-sales.png"), iconColor: "#BCE9FF" },
    { title: "Sales Period Covered", value: formatCurrency(m?.salesPeriodCovered) ?? "PHP0.00", icon: require("../../icons/inventory/dashboard/weekly-sales.png"), iconColor: "#FFDFC0" },
    { title: "Total Supplier", value: m?.totalSupplier ?? m?.totalSuppliers ?? "0", icon: require("../../icons/inventory/dashboard/supplier.png"), iconColor: "#C9D2FF" },
    { title: "Total Profit", value: formatCurrency(m?.totalProfit) ?? "PHP0.00", icon: require("../../icons/inventory/dashboard/pending-orders.png"), iconColor: "#FFE7A8" },
    { title: "Procurement Cost", value: formatCurrency(m?.procurementCost) ?? "PHP0.00", icon: require("../../icons/inventory/dashboard/approved-orders.png"), iconColor: "#FFDDE0" },
  ];

  // Orders from API dashboard.orders
  const apiOrders = dashboard?.orders ?? [];
  const ordersData = (Array.isArray(apiOrders) && apiOrders.length > 0)
    ? apiOrders.map((o, i) => {
        const issued = o.dateIssued ? new Date(o.dateIssued) : null;
        const issuedStr = issued && !Number.isNaN(issued.getTime())
          ? issued.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
          : "-";
        const eta = o.deliveryETA ? new Date(o.deliveryETA) : null;
        const etaStr = eta && !Number.isNaN(eta.getTime())
          ? eta.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
          : "-";
        return {
          id: o.orderId ?? i + 1,
          dateIssued: issuedStr,
          orderId: String(o.orderId ?? "-"),
          merchantName: o.merchantName ?? "-",
          orderType: o.orderType ?? "-",
          facilities: o.facilities ?? o.facility ?? "-",
          orderStatus: o.orderStatus ?? "-",
          deliveryETA: etaStr
        };
      })
    : Array.from({ length: 10 }, (_, i) => ({
        id: i + 1,
        dateIssued: "",
        orderId: "",
        merchantName: "",
        orderType: "",
        facilities: "",
        orderStatus: "",
        deliveryETA: ""
      }));

  // Activity log from API dashboard.activityLog
  const apiActivity = dashboard?.activityLog ?? [];
  const activityData = (Array.isArray(apiActivity) && apiActivity.length > 0)
    ? apiActivity.map((a, i) => ({
        id: i + 1,
        timestamp: a.timestamp ? new Date(a.timestamp).toLocaleString() : "-",
        activity: a.activity ?? "-",
        user: {
          name: a.userFullName ?? "",
          email: a.userEmail ?? "",
          avatar: a.userFullName ? `https://ui-avatars.com/api/?name=${encodeURIComponent(a.userFullName)}&background=random` : ""
        },
        details: a.details ?? ""
      }))
    : Array.from({ length: 5 }, (_, i) => ({
        id: i + 1,
        timestamp: "",
        activity: "",
        user: { name: "", email: "", avatar: "" },
        details: ""
      }));

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
        <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>Loading...</div>
      </div>
    );
  }

  if (activeView === "ytd-sales") {
    return (
      <YearToDateSalesReport
        onBack={() => setActiveView("overview")}
        dashboard={dashboard}
        metrics={m}
        backButtonIcon={backButtonIcon}
        facilityId={facility}
      />
    );
  }

  if (activeView === "sales-period") {
    return (
      <SalesPeriodCoveredReport
        onBack={() => setActiveView("overview")}
        dashboard={dashboard}
        metrics={m}
        backButtonIcon={backButtonIcon}
        facilityId={facility}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />
    );
  }

  if (activeView === "total-profit") {
    return (
      <TotalProfitReport
        onBack={() => setActiveView("overview")}
        dashboard={dashboard}
        metrics={m}
        backButtonIcon={backButtonIcon}
        facilityId={facility}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />
    );
  }

  if (activeView === "procurement-cost") {
    return (
      <ProcurementCostReport
        onBack={() => setActiveView("overview")}
        dashboard={dashboard}
        metrics={m}
        backButtonIcon={backButtonIcon}
        facilityId={facility}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />
    );
  }

  return (
    <div className="dashboard-content">
      <div className="dashboard-header dashboard-header-with-controls">
        <div className="dashboard-title-group">
          <h1 className="dashboard-title">Dashboard</h1>
          <div className="dashboard-facility-dropdown">
            <select
              className="dashboard-facility-select"
              value={facility}
              onChange={(e) => setFacility(e.target.value)}
            >
              {facilities.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="dashboard-facility-icon" size={18} />
          </div>
        </div>
        <div className="dashboard-header-controls">
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onClear={clearNotification}
          />
          <div className="dashboard-date-filters">
            <div className="dashboard-date-filter">
              <Calendar size={18} className="dashboard-date-icon" />
              <input
                type="date"
                className="dashboard-date-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
              />
            </div>
            <div className="dashboard-date-filter">
              <Calendar size={18} className="dashboard-date-icon" />
              <input
                type="date"
                className="dashboard-date-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="dashboard-cards-grid">
        {dashboardCards.map((card, index) => (
          <DashboardCard
            key={index}
            title={card.title}
            value={card.value}
            icon={card.icon}
            iconColor={card.iconColor}
            onClick={
              card.title === "Year to Date Sales"
                ? () => setActiveView("ytd-sales")
                : card.title === "Sales Period Covered"
                ? () => setActiveView("sales-period")
                : card.title === "Total Profit"
                ? () => setActiveView("total-profit")
                : card.title === "Procurement Cost"
                ? () => setActiveView("procurement-cost")
                : undefined
            }
          />
        ))}
      </div>
      
      <div className="dashboard-charts-grid">
        <DashboardChart
          title="Sales Overview and Gross Profit Analysis"
          type="bar"
          data={
            salesViewMode === "Monthly"
              ? ((dashboard?.graph?.monthly && dashboard.graph.monthly.length > 0)
                  ? dashboard.graph.monthly.map((row) => ({
                      label: row.month ?? "",
                      sales: Number(row.sales) || 0,
                      profit: Number(row.profit) || 0
                    }))
                  : [])
              : salesViewMode === "Weekly"
              ? ((dashboard?.graph?.weekly && dashboard.graph.weekly.length > 0)
                  ? dashboard.graph.weekly.map((row) => ({
                      label: row.week ?? "",
                      sales: Number(row.sales) || 0,
                      profit: Number(row.profit) || 0
                    }))
                  : [])
              : ((dashboard?.graph?.daily && dashboard.graph.daily.length > 0)
                  ? dashboard.graph.daily.map((row) => ({
                      label: row.day ?? "",
                      sales: Number(row.sales) || 0,
                      profit: Number(row.profit) || 0
                    }))
                  : [])
          }
          xAxisDataKey="label"
          bars={[
            { dataKey: "sales", fill: "#3b82f6", name: "Sales" },
            { dataKey: "profit", fill: "#22c55e", name: "Profit" }
          ]}
          dropdownOptions={["Daily", "Weekly", "Monthly"]}
          activeDropdown={salesViewMode}
          onDropdownChange={setSalesViewMode}
        />
      </div>
      
      <OrdersTable
        ordersData={ordersData}
        onViewOrder={(row) => {
          const id = row?.id ?? row?.orderId;
          if (!id) return;
          onViewOrder?.(id);
        }}
      />
      <ActivityLog activityData={activityData} />
    </div>
  );
};

export default Dashboard;
